import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { TwScope } from "../../../components/ui/TwScope";
import CompanyDetailsModal from "../companyDetails/CompanyDetailsModal";
import CompanyEditModal from "../companyEdit/CompanyEditModal";
import GenericProcessModal from "../genericProcessModal/GenericProcessModal";
import CompanyRegisterModal from "../companyRegister/CompanyRegisterModal";
import RecipeIngredientModal from "../recipeIngredient/RecipeIngredientModal";
import LoadingOverlay from "../shared/components/LoadingOverlay";
import CompanyListHeader from "./components/CompanyListHeader";
import CompanyListPagination from "./components/CompanyListPagination";
import CompanyTable from "./components/CompanyTable";
import { useCompanyListViewModel } from "./hooks/useCompanyListViewModel";

/**
 * Tailwind rebuild of the Company grid, following the pattern established by
 * `ProductListNew` (src/screens/lcaProduct/productList/ProductListNew.tsx)
 * and `SupplierListNew`: a `TwScope` page shell composing
 * header/table/pagination, all state/data-fetching living in
 * `useCompanyListViewModel`. Gated behind `ENABLE_LCA_COMPANY_UI_REDESIGN` —
 * see src/routes/companyRoutes.tsx for the switch against the untouched
 * legacy `CompanyList` (src/screens/company/companyList/CompanyList.tsx).
 *
 * Every legacy capability is kept: register / view / edit / delete, the
 * page-size selector, and — behind `DISABLE_COMPANY_LIST_CREATE_ON_COLUMN`,
 * as before — the per-company "Recipe ingredients" and "Generic process"
 * allow-list modals.
 */
const CompanyListNew = () => {
  const vm = useCompanyListViewModel();

  return (
    <TwScope className="tw-min-h-full tw-bg-white tw-p-6">
      <CompanyListHeader onRegisterClick={vm.openRegisterModal} />

      <CompanyTable
        rows={vm.rows}
        isLoading={vm.isRowsLoading}
        onView={vm.openDetailsModal}
        onEdit={vm.openEditModal}
        onDelete={vm.openDeleteConfirm}
        onAddRecipeIngredients={vm.openRecipeModal}
        onAddGenericProcess={vm.openGenericProcessModal}
      />

      <CompanyListPagination
        totalCount={vm.totalCount}
        pageNumber={vm.pageNumber}
        totalPages={vm.totalPages}
        pageSize={vm.pageSize}
        onPageChange={vm.handlePagination}
        onPageSizeChange={vm.handlePageSize}
      />

      <CompanyRegisterModal
        visibleModal={vm.visibleRegisterModal}
        closeModal={vm.closeRegisterModal}
        onSuccess={vm.onSuccessRegister}
      />

      <CompanyDetailsModal
        visibleModal={vm.visibleDetailsModal}
        id={vm.detailsId}
        setVisibleModal={vm.closeDetailsModal}
        setVisibleEdit={vm.navigateDetailsToEdit}
      />

      <CompanyEditModal
        visibleModal={vm.visibleEditModal}
        id={vm.editId}
        closeModal={vm.closeEditModal}
        onSuccess={vm.onSuccessEdit}
      />

      <RecipeIngredientModal company={vm.recipeCompany} closeModal={vm.closeRecipeModal} />

      <GenericProcessModal
        company={vm.genericProcessCompany}
        closeModal={vm.closeGenericProcessModal}
      />

      <ConfirmModal
        viewModal={vm.visibleDeleteConfirm}
        closeModal={vm.closeDeleteConfirm}
        cancel={vm.closeDeleteConfirm}
        approve={vm.confirmDelete}
        title="Delete Company"
        subTitle="Are you sure you want to delete your company?"
      />

      {vm.isDeleting ? <LoadingOverlay /> : null}
    </TwScope>
  );
};

export default CompanyListNew;
